import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {environment} from '../../environments/environment';
import {HttpClient, HttpHeaders, HttpParams} from '@angular/common/http';
import {AuthService} from '../authentication/auth.service';
import {AlertLimit, Device, GraphEntity, Kit, KitModel, LoginResponse, MagmaResponse, Page} from '../models';
import {NgxSpinnerService} from 'ngx-spinner';
import {finalize, map} from 'rxjs/operators';
import Cookies from "js-cookie";
import { hasPackagePermission } from '../authentication/package.permission';

@Injectable({
  providedIn: 'root'
})
export class AccountService {
  deviceName = new BehaviorSubject('');
  clientId = '';
  clients: any[] = [];

  constructor(private http: HttpClient, private spinner: NgxSpinnerService ) {
  }


  forgotPassword(entity: any) {
    return this.http.post<any>(
      environment.host + '/request-reset-password', entity,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json'
          }),
      }
    );
  }

  changePassword(value: any): Observable<any> {
    return this.http.post<any>(
      environment.host + '/change-password',
      value,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  createDevice(device: Device): Observable<MagmaResponse<Device>> {
    return this.http.post<MagmaResponse<Device>>(
      environment.host + '/core/device',
      device,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  setUpUserNamePassword(data: any):Observable<any>{
    return this.http.post<any>(
      environment.host + '/user-name-password',
      data,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    )
  }

  createDeviceWithCreatedBy(device: Device): Observable<MagmaResponse<Device>> {
    let url: string;

    if (!hasPackagePermission("isNotAzureUser")) {
      url = `${environment.host}/core/user/${Cookies.get("USER_ID")}/device`
    } else {
      url = environment.enableUserLog ? `${environment.host}/core/user/${Cookies.get("USER_ID")}/device` : `${environment.host}/core/device`
    }
    return this.http.post<MagmaResponse<Device>>(
      url,
      device,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  getBatches(): Observable<any> {
    return this.http.get<any>(
      environment.host + `/core/user/${Cookies.get('USER_ID')}/deviceTest/batchNumbers`,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    )
  }

  runMockDevice(deviceId, clientId): Observable<any> {
    return this.http.put<any>(
      environment.host + '/portal/device/' + deviceId + '/jar-runner?clientId=' + clientId,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          }),
      }
    )
  }
  updateDeviceMode(deviceId, status): Observable<any> {
    return this.http.put<any>(
      environment.host + '/core/device/' + deviceId + '/mode',
      status,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          }),
      }
    )
  }

  updateDevice(deviceId: string, device: Device): Observable<MagmaResponse<Device>> {
    return this.http.put<MagmaResponse<Device>>(
      environment.host + '/core/device/' + deviceId,
      device,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  updateDeviceWithModifiedBy(deviceId: string, device: Device): Observable<MagmaResponse<Device>> {
    const url = environment.enableUserLog ? `${environment.host}/core/user/${Cookies.get("USER_ID")}/device/${deviceId}` : `${environment.host}/core/device/${deviceId}`
    return this.http.put<MagmaResponse<Device>>(
      url,
      device,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  updateLabels(deviceId, labelChangeDetails: { sensorDTOS: { id: string; label: string; }[]; sensorCodeDTOS: any }): Observable<object> {
    return this.http.put(
      environment.host + '/core/device/' + deviceId + '/sensor/changeLabels',
      labelChangeDetails,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
        }),
      }
    );
  }


  getDevice(id): Observable<any> {
    return this.http.get<MagmaResponse<Device>>(
      environment.host + '/core/device/' + id,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getBEVersion(): Observable<any> {
    return this.http.get<MagmaResponse<String>>(
      environment.host + '/core/open/info' ,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getAppBEVersion(): Observable<any> {
    return this.http.get<MagmaResponse<String>>(
      environment.host + '/open/info' ,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  deleteDevice(id: string): Observable<MagmaResponse<any>> {
    return this.http.delete<MagmaResponse<any>>(
      environment.host + '/core/device/' + id,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  deleteDPDevice(id): Observable<any> {
    return this.http.delete<MagmaResponse<any>>(
      environment.host + '/core/user/' + Cookies.get("USER_ID") + 'device/' + id,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getDevices(pageIndex?: number, pageSize?: number, magmaCodecId?: string, searchTerm?: string, validity?: string, returnType?: string): Observable<MagmaResponse<Page<Device>>> {
    let params = new HttpParams();
    if (pageIndex !== undefined) params = params.set('pageIndex', pageIndex.toString());
    if (pageSize !== undefined) params = params.set('pageSize', pageSize.toString());
    if (magmaCodecId) params = params.set('magmaCodecId', magmaCodecId);
    if (searchTerm) params = params.set('searchTerm', searchTerm);
    if (validity) params = params.set('validity', validity);
    if (returnType) params = params.set('returnType', returnType);

    return this.http.get<MagmaResponse<Page<Device>>>(
      environment.host + '/core/device',
      {
        params,
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + AuthService.getAccessToken()
        })
      }
    );
  }

  getAllDevices(from?: string, to?: string): Observable<any> {
    let url = environment.host + '/core/user/' + Cookies.get("USER_ID") + '/device';
    if (from && to) {
      url += `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }
    return this.http.get<MagmaResponse<Device[]>>(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + AuthService.getAccessToken()
      })
    });
  }

  getAllArchivedDevices(planId: string, from?: string, to?: string): Observable<any> {
    let url = environment.host + '/core/user/' + Cookies.get("USER_ID") + '/deviceArchives';
    
    let params = new HttpParams().set('planId', planId);
    
    if (from) {
        params = params.set('from', from);
    }
    if (to) {
        params = params.set('to', to);
    }
    
    return this.http.get<MagmaResponse<any>>(url, {
        params,
        headers: new HttpHeaders({
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
        })
    });
  }

  getAllUsers(from?: string, to?: string): Observable<any> {
    let url = environment.host + '/ums/user/' + Cookies.get("USER_ID") + '/system-user?filter=device-pulse';
    if (from && to) {
      url += `&from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    }
    return this.http.get<MagmaResponse<any>>(url, {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        Authorization: 'Bearer ' + AuthService.getAccessToken()
      })
    });
  }

  getFavouriteDevices(): Observable<any> {
    return this.http.get<MagmaResponse<Device[]>>(
      environment.host + '/core/user/' + Cookies.get("USER_ID") + '/device/favourite',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getSubscriptionDetails(): Observable<any> {
    return this.http.get<MagmaResponse<any>>(
      environment.host + '/ums/user/' + Cookies.get("USER_ID") + '/azureSubscription',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  addOrRemoveFavourite(id, action): Observable<any> {
    return this.http.put<MagmaResponse<Device[]>>(
      environment.host + '/core/user/' + Cookies.get("USER_ID") + '/device/' + id + "?action=" + action,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }


  getOneDevice(coperateId, filter, favourite): Observable<MagmaResponse<any>> {
    const userId = Cookies.get('USER_ID')
    if (coperateId === undefined && filter === undefined && favourite === undefined) {
      return this.getDevices();
    }
    if (filter === "SensorFailure&sensor" || filter === "ActiveSensors&sensor" || filter === "WrongValued&sensor") {
      return this.http.get<MagmaResponse<Device[]>>(
        environment.host + "/user/" + userId + "/corporate/" + coperateId + "/corporateDevices?dataFilter=" + filter + "=" + favourite,
        {
          headers: new HttpHeaders(
            {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + AuthService.getAccessToken()
            })
        }
      );
    } else {
      return this.http.get<MagmaResponse<Device[]>>(
        environment.host + "/user/" + userId + "/corporate/" + coperateId + "/corporateDevices?dataFilter=" + filter + "&favouriteFilter=" + favourite,
        {
          headers: new HttpHeaders(
            {
              'Content-Type': 'application/json',
              Authorization: 'Bearer ' + AuthService.getAccessToken()
            })
        }
      );
    }
  }

  createKit(kit: any): Observable<any> {
    const kitModelId = kit.kitModel[0].item_id;
    return this.http.post<any>(
      environment.host + '/core/kit-model/' + kitModelId + '/kit',
      kit,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  createKitWithCreatedBy(kit: any): Observable<any> {
    const kitModelId = kit.kitModel[0].item_id;
    const url = environment.enableUserLog ? `${environment.host}/core/user/${Cookies.get("USER_ID")}/kit-model/${kitModelId}/kit` : `${environment.host}//core/kit-model/${kitModelId}/kit`
    return this.http.post<any>(
      url,
      kit,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  updateKit(kitId, kit: any): Observable<any> {
    return this.http.put<any>(
      environment.host + '/core/kit/' + kitId,
      kit,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  updateKitWithModifiedBy(kitId, kit: any): Observable<any> {
    const url = environment.enableUserLog ? `${environment.host}/core/user/${Cookies.get("USER_ID")}/kit/${kitId}` : `${environment.host}/core/kit/${kitId}`
    return this.http.put<any>(
      url,
      kit,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  getReferenceImages(kitId: string, kit: any): Observable<any> {
    return this.http.get<any>(
      environment.host + '/user/' + Cookies.get("USER_ID") + '/kit/' + kitId + '/referenceImage',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  updateReferenceImageStatus(kitId: string, status: string): Observable<any> {
    return this.http.put<any>(
      environment.host + '/user/' + Cookies.get("USER_ID") + '/kit/' + kitId + '/referenceImageStatus',
      {}, 
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${AuthService.getAccessToken()}`
        }),
        params: new HttpParams().set('status', status)
      }
    );
  }

  getKits(): Observable<any> {
    return this.http.get<MagmaResponse<Kit[]>>(
      environment.host + '/core/kit?returnType=LIST',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getKit(id): Observable<any> {
    return this.http.get<MagmaResponse<Kit>>(
      environment.host + '/core/kit/' + id,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  deleteKit(id): Observable<any> {
    return this.http.delete<MagmaResponse<any>>(
      environment.host + '/core/kit/' + id,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  createKitModel(kitModel: any): Observable<any> {

    if (kitModel.properties.includes('B')) {


    }
    return this.http.post<any>(
      environment.host + '/core/kit-model',
      kitModel,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  updateKitModel(kitModelId, kitModel: any): Observable<any> {
    return this.http.put<any>(
      environment.host + '/core/kit-model/' + kitModelId,
      kitModel,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  getKitModel(id): Observable<any> {
    return this.http.get<MagmaResponse<KitModel>>(
      environment.host + '/core/kit-model/' + id,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  deleteKitModel(id): Observable<any> {
    return this.http.delete<MagmaResponse<any>>(
      environment.host + '/core/kit-model/' + id,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getKitModels(): Observable<any> {
    return this.http.get<MagmaResponse<KitModel[]>>(
      environment.host + '/core/kit-model?returnType=LIST',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  createAlertLimitsForKit(kitId, alertLimit: any): Observable<any> {
    return this.http.post<any>(
      environment.host + '/core/kit/' + kitId + '/property/1/alert-limit',
      alertLimit,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  getOfflineAlertsOfKit(kitId, from, to): Observable<any> {
    return this.http.get<MagmaResponse<any>>(
      environment.host + '/core/kit/' + kitId + '/offline-alerts' + '?from=' + from + '&to=' + to,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  updatePersistence(kitId, value: any): Observable<any> {
    return this.http.put<any>(
      environment.host + '/core/kit/' + kitId + '/persistence',
      value,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  updateMaintenance(kitId, value: any): Observable<any> {
    return this.http.put<any>(
      environment.host + '/core/kit/' + kitId + '/maintain',
      value,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getMetaData(): Observable<any> {
    // return [];
    return this.http.get<MagmaResponse<any>>(
      environment.host + '/core/meta-data',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  // from polar project service

  getProject(projectId): Observable<any> {
    return this.http.get<any>(
      environment.host + '/user/' + AuthService.getUserId() + '/project/' + projectId,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getAlertLimitForProperty(deviceId, propertyId): Observable<MagmaResponse<AlertLimit[]>> {
    return this.http.get<MagmaResponse<AlertLimit[]>>(
      environment.host + '/user/' + AuthService.getUserId() + '/kit/' + deviceId + '/property/' + propertyId + '/alert-limit',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getDeviceHistory(deviceId, from, to, factor): Observable<MagmaResponse<GraphEntity[]>> {
    return this.http.get<MagmaResponse<GraphEntity[]>>(
      environment.host + '/user/' + AuthService.getUserId() + '/kit/' + deviceId + '/property/' + factor + '?from=' + from + '&to=' + to,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getDeviceHistoryAll(deviceId, from, to): Observable<MagmaResponse<GraphEntity[]>> {
    return this.http.get<MagmaResponse<any>>(
      // tslint:disable-next-line:max-line-length
      environment.host + '/core/kit/' + deviceId + '/graph-kit-history/' + '?from=' + from + '&to=' + to,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getSensorData(deviceId, sensorNumber, from, to) {
    return this.http.get<MagmaResponse<any>>(
      environment.host + '/core/device/' + deviceId + '/sensor/' + sensorNumber + '?from=' + from + '&to=' + to + '&order=' + 'DESC',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getMaintenanceData(deviceId, sensorNumber, from, to) {
    return this.http.get<MagmaResponse<any>>(
      environment.host + '/core/device/' + deviceId + '/maintenance/' + sensorNumber + '?from=' + from + '&to=' + to + '&order=' + 'DESC',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getActuatorData(deviceId, sensorNumber, from, to) {
    return this.http.get<MagmaResponse<any>>(
      environment.host + '/core/device/' + deviceId + '/actuator/' + sensorNumber + '?from=' + from + '&to=' + to + '&order=' + 'DESC',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  getDevicesSummary() {
    return this.http.get<MagmaResponse<any>>(
      environment.host + '/core/devices-summary',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  updateConnectivity(deviceId, body: any): Observable<any> {
    return this.http.post<any>(
      environment.host + '/core/device/' + deviceId + '/connectivity',
      body,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    );
  }

  getConnectivity(deviceId): Observable<any> {
    return this.http.get<MagmaResponse<any>>(
      environment.host + '/core/device/' + deviceId + '/connectivity',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + AuthService.getAccessToken()
          })
      }
    );
  }

  createVmqAcl(inputValue: any): Observable<any> {

    return this.http.post<any>(
      environment.host + '/core/vernemq-acl', inputValue,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    )
  }

  getAllVmqAcl(): Observable<any> {

    return this.http.get<any>(
      environment.host + '/core/vernemq-acl?returnType=LIST',
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    )
  }

  getClientsWithProtocolAndDeviceCountAzureUser(): Observable<any> {

    return this.http.get<any>(
      environment.host + `/core/user/${Cookies.get("USER_ID")}/all-connectivity?returnType=LIST`,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    )
  }

  getCurrentAmqAcl(id): Observable<any> {
    return this.http.get(
      environment.host + '/core/vernemq-acl/' + id,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        ),
      }
    )
  }

  deleteVmqAcl(id) {
    return this.http.delete(
      environment.host + '/core/vernemq-acl/' + id,
      {
        headers: new HttpHeaders({
          'Content-Type': 'application/json',
          Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
        })
      }
    )
  }

  updatedVernemq(id, data: any): Observable<any> {
    return this.http.put<any>(
      environment.host + '/core/vernemq-acl/' + id,
      data,
      {
        headers: new HttpHeaders(
          {
            'Content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }),
      }
    )
  }

  getClientsWithProtocolAndDeviceCount() {
    return this.http.get<any>(
      environment.host + '/core/all-connectivity?returnType=LIST',
      {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      }
    )
  }

  createHttpAclAuth(data: any) {
    return this.http.post<any>(
      environment.host + '/core/http-acl', data, {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      }
    )
  }

  updateHttpClient(data, id) {
    return this.http.put<any>(
      environment.host + '/core/http-acl/' + id, data, {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      }
    )
  }

  deleteHttpClient(id) {
    return this.http.delete(
      environment.host + '/core/http-acl/' + id, {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      }
    )
  }


  getAllHttpAcl() {
    return this.http.get(
      environment.host + '/core/http-acl?returnType=LIST', {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      }
    )
  }

  updateActionKey(id, status) {
    const url = `${environment.host}/core/connectivity/mqtt-status/${id}?status=${status}`;
    return this.http.put<any>(
      url, null, {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      }
    )
  }

  updateActionKeyHttp(id, status) {
    const url = `${environment.host}/core/connectivity/httpclient-status/${id}?status=${status}`;
    return this.http.put<any>(
      url, null, {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      }
    )
  }

  getAccessToken(id) {
    return this.http.post<any>(environment.host + `/core/http-acl/${id}/access-token`,{},
      {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      })
  }

  getNotifications (start,end) {
    const url = `${environment.host}/core/allRootCauseResults?startingIndex=${start}&endIndex=${end}`;
    return this.http.get<any>(
      url, {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json',
            Authorization: 'Bearer ' + Cookies.get("ACCESS_TOKEN"),
          }
        )
      }
    )
  }

  resetPassword(entity){
    return this.http.post<any>(
      `${environment.host}/reset-password`,
      entity,
      {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json'
          }
        )
      }
    )
  }

  validateHashKey(key,username,email){
    const url = username != null ?`${environment.host}/ums/validate-key?hash=${key}&username=${username}` : `${environment.host}/ums/validate-key?hash=${key}&email=${email}`
    return this.http.get<any>(
      url,
      {
        headers: new HttpHeaders(
          {
            'content-Type': 'application/json'
          }
        )
      }
    )
  }
}
